import { __DEV__ } from "@/constants";

export type LogLevel = "debug" | "info" | "warn" | "error";

type LogFn = (msg: string, attrs?: Record<string, any>) => void;

declare global {
	interface Window {
		logger: ReturnType<typeof _useLogger>;
	}
}

interface LogConfig {
	color: string;
}

function _useLogger() {
	let currentLogLevel: LogLevel = "warn";
	let displayLine = false;

	const LogStyles: Record<LogLevel, LogConfig> = {
		debug: { color: "#0ea5e9" },
		info: { color: "#10b981" },
		warn: { color: "#d97706" },
		error: { color: "#dc2626" },
	};

	function setLevel(level: LogLevel) {
		currentLogLevel = level;
	}

	function toggleLineDisplay() {
		displayLine = !displayLine;
	}

	function getLevelWeight(level: LogLevel): number {
		switch (level) {
			case "debug":
				return 0;
			case "info":
				return 1;
			case "warn":
				return 2;
			case "error":
				return 3;
		}
	}

	function levelActive(level: LogLevel): boolean {
		return getLevelWeight(level) >= getLevelWeight(currentLogLevel);
	}

	function log(level: LogLevel, msg: string, attrs?: Record<string, any>) {
		if (!levelActive(level)) return;

		if (displayLine) {
			// Create an error to retrive the stack call
			// The line 3 is the original file location
			const line = new Error().stack?.split("\n").at(3)?.trim();

			// Extract the file name from the stack line
			const file = /\((.*)\)/g.exec(line || "")?.at(1);

			msg += ` at (${file})`;
		}

		const { color } = LogStyles[level];
		const style = `color: ${color}; font-weight: bold;`;

		const outputMsg = `%c[${level.toUpperCase()}] ${msg}`;

		if (attrs != null) {
			console[level](outputMsg, style, attrs);
		} else {
			console[level](outputMsg, style);
		}
	}

	function makeLogFn(level: LogLevel): LogFn {
		return (msg, attrs) => log(level, msg, attrs);
	}

	return {
		setLevel,
		toggleLineDisplay,
		debug: makeLogFn("debug"),
		info: makeLogFn("info"),
		warn: makeLogFn("warn"),
		error: makeLogFn("error"),
	};
}

export const useLogger = () => {
	if (window.logger == null) {
		window.logger = _useLogger();

		if (__DEV__) {
			window.logger.setLevel("debug");
		}
	}

	return window.logger;
};
